import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api/api';
import FAQHelpSection from '../components/FAQHelpSection';

const EmailVerificationPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    document.title = 'メール認証 | はしもと栄養パートナー';
  }, []);

  useEffect(() => {
    verifyEmail();
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await api.get(`/api/auth/verify-email/${token}`);
      
      if (response.data.success) {
        setStatus('success');
        setMessage(response.data.message);
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (error) {
      console.error('Email verification failed:', error);
      setStatus('error');
      setMessage(error.response?.data?.message || 'メール確認に失敗しました');
    }
  };

  const resendVerificationEmail = async () => {
    setIsResending(true);
    try {
      // We'll need the user's email for this - for now, show a form
      const userEmail = prompt('メールアドレスを入力してください:');
      if (!userEmail) {
        setIsResending(false);
        return;
      }

      const response = await api.post('/api/auth/resend-verification', {
        email: userEmail
      });

      if (response.data.success) {
        alert('確認メールを再送信しました。メールをご確認ください。');
      }
    } catch (error) {
      console.error('Resend verification failed:', error);
      alert(error.response?.data?.message || 'メール再送信に失敗しました');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            はしもと栄養パートナー
          </h1>
          <h2 className="text-2xl font-semibold text-gray-700">
            メールアドレス確認
          </h2>
        </div>

        <div className="bg-white shadow-md rounded-lg p-6">
          {status === 'verifying' && (
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">メールアドレスを確認中です...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">確認完了</h3>
              <p className="text-gray-600 mb-4">{message}</p>
              <p className="text-sm text-gray-500">3秒後にログインページに移動します...</p>
              
              <div className="mt-6">
                <Link 
                  to="/login" 
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  今すぐログイン
                </Link>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">確認に失敗しました</h3>
              <p className="text-gray-600 mb-4">{message}</p>
              
              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4 mb-4">
                <h4 className="font-bold text-yellow-800 mb-2 flex items-center">
                  💡 メール再送信について
                </h4>
                <div className="bg-yellow-100 border border-yellow-300 rounded-md p-3 mb-3">
                  <p className="font-bold text-yellow-900 text-sm">
                    📧 再送信後は<strong>迷惑メール・スパムフォルダ</strong>を必ずご確認ください！
                  </p>
                </div>
                <ul className="text-sm text-yellow-700 text-left space-y-1">
                  <li>• Gmail・Yahoo!メールなどで迷惑メールに分類される可能性があります</li>
                  <li>• メールアドレスの入力に間違いがないかご確認ください</li>
                </ul>
              </div>

              <div className="space-y-3">
                <button
                  onClick={resendVerificationEmail}
                  disabled={isResending}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isResending ? '送信中...' : '確認メールを再送信'}
                </button>
                
                <Link 
                  to="/register" 
                  className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  新規登録に戻る
                </Link>
              </div>
            </div>
          )}
        </div>

        <div className="text-center">
          <Link to="/" className="text-sm text-gray-600 hover:text-gray-900">
            ← ホームに戻る
          </Link>
        </div>
        
        {/* FAQ Help Section - メール認証で困った時 */}
        <div className="mt-8">
          <FAQHelpSection 
            title="メール認証でお困りですか？"
            message="認証メールが届かない、エラーが出るなどの問題の解決策をまとめています"
            variant="warning"
          />
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPage;